"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { nanoid } from "nanoid";
import { useStream } from "@/hooks/useStream";
import { useSession } from "@/hooks/useSession";
import { createSession } from "@/lib/session";
import { getRelativeTime } from "@/lib/utils";
import EmailCaptureForm from "@/components/EmailCaptureForm";
import { captureEvent } from "@/lib/posthog";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import PhaseIndicator from "./PhaseIndicator";
import SearchingIndicator from "./SearchingIndicator";
import ScorePanel from "./ScorePanel";
import DownloadButton from "./DownloadButton";
import FullBundlePointer from "./FullBundlePointer";
import type {
  Message,
  ValidationSession,
  StreamEvent,
  DiscoveryPhase,
  KillSignal,
  ConfidenceScores,
} from "@/types";

const MAX_CHARS = 2000;

// ─── Initial Idea Input ────────────────────────────────────────────────────────

function IdeaInputForm({
  onStart,
  onResume,
}: {
  onStart: (idea: string) => void;
  onResume: () => void;
}) {
  const [idea, setIdea] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { session, clearSession } = useSession();

  const hasIncompleteSession = session && session.phase !== "complete";

  useEffect(() => {
    if (!hasIncompleteSession) {
      textareaRef.current?.focus();
    }
  }, [hasIncompleteSession]);

  const charCount = idea.length;
  const charCountColor =
    charCount > MAX_CHARS
      ? "var(--color-contradicted-fg)"
      : charCount > 1800
        ? "var(--color-weak-fg)"
        : "var(--text-muted)";

  const handleSubmit = () => {
    const trimmed = idea.trim();
    if (trimmed.length < 10) {
      setError("Tell us a bit more about the idea");
      return;
    }
    if (trimmed.length > MAX_CHARS) {
      setError("Please keep your idea under 2000 characters");
      return;
    }
    setError(null);
    onStart(trimmed);
  };

  const handleClearAndStart = () => {
    clearSession();
    setShowConfirm(false);
    textareaRef.current?.focus();
  };

  // If there's an existing incomplete session — show resume prompt
  if (hasIncompleteSession) {
    const sessionMeta = session;

    function getPhaseLabel(phase: DiscoveryPhase): string {
      switch (phase) {
        case "brain_dump":
        case "discovery": return "Discovery in progress";
        case "research": return "Research in progress";
        case "findings": return "Research complete";
        case "complete": return "Results ready";
      }
    }

    function truncate(s: string, max = 80): string {
      if (s.length <= max) return s;
      const t = s.slice(0, max);
      const last = t.lastIndexOf(" ");
      return (last > 60 ? t.slice(0, last) : t) + "...";
    }

    return (
      <div
        className="mx-auto w-full px-[var(--space-4)] md:px-[var(--space-8)] py-[var(--space-10)]"
        style={{ maxWidth: "720px" }}
      >
        <p className="section-label mb-[var(--space-4)]">PREVIOUS SESSION</p>

        <div
          className="rounded-[var(--radius-lg)] border p-[var(--space-5)] mb-[var(--space-6)]"
          style={{
            backgroundColor: "var(--bg-surface)",
            borderColor: "var(--border-default)",
          }}
        >
          <p
            className="font-sans mb-[var(--space-2)]"
            style={{ fontSize: "var(--text-base)", color: "var(--text-primary)" }}
          >
            {truncate(sessionMeta.ideaSummary)}
          </p>
          <p
            className="font-sans mb-[var(--space-1)]"
            style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}
          >
            Status: {getPhaseLabel(sessionMeta.phase)}
          </p>
          <p
            className="font-sans"
            style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}
          >
            Last active: {getRelativeTime(sessionMeta.updatedAt)}
          </p>
        </div>

        <div className="flex items-center gap-[var(--space-4)] mb-[var(--space-4)]">
          <button
            onClick={onResume}
            className="accent-btn inline-flex items-center justify-center px-[var(--space-6)] py-[var(--space-3)] rounded-[var(--radius-md)] font-sans text-sm font-semibold uppercase tracking-[0.08em]"
          >
            RESUME SESSION
          </button>

          {!showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              className="font-sans text-sm"
              style={{ color: "var(--text-secondary)" }}
            >
              Start fresh
            </button>
          ) : (
            <div className="flex items-center gap-[var(--space-3)] flex-wrap">
              <span className="font-sans text-sm" style={{ color: "var(--text-secondary)" }}>
                Start fresh? Your previous session will be cleared.
              </span>
              <button
                onClick={handleClearAndStart}
                className="accent-btn inline-flex items-center justify-center px-[var(--space-4)] py-[var(--space-2)] rounded-[var(--radius-md)] font-sans text-xs font-semibold uppercase tracking-[0.08em]"
              >
                CONFIRM
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="outline-btn inline-flex items-center justify-center px-[var(--space-4)] py-[var(--space-2)] rounded-[var(--radius-md)] font-sans text-xs font-medium uppercase tracking-[0.08em] border"
              >
                CANCEL
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // New session input form
  return (
    <div
      className="mx-auto w-full px-[var(--space-4)] md:px-[var(--space-8)] py-[var(--space-10)]"
      style={{ maxWidth: "720px" }}
    >
      <p className="section-label mb-[var(--space-6)]">FULL VALIDATION</p>

      <div className="mb-[var(--space-4)]">
        <label htmlFor="validate-idea" className="sr-only">
          Describe your product idea
        </label>
        <textarea
          id="validate-idea"
          ref={textareaRef}
          value={idea}
          onChange={(e) => {
            setIdea(e.target.value.slice(0, MAX_CHARS));
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          maxLength={MAX_CHARS}
          placeholder="What's the idea? Tell me what it does, who it's for, and what problem it solves. Raw is fine — we'll dig in together."
          className="w-full rounded-[var(--radius-md)] border font-sans resize-vertical"
          style={{
            minHeight: "160px",
            backgroundColor: "var(--bg-surface)",
            borderColor: error ? "var(--color-contradicted-fg)" : "var(--border-default)",
            color: "var(--text-primary)",
            fontSize: "var(--text-base)",
            lineHeight: "var(--leading-relaxed)",
            padding: "var(--space-4)",
          }}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? "validate-idea-error" : "validate-idea-count"}
        />
      </div>

      <div className="flex items-center justify-between mb-[var(--space-4)]">
        <div>
          {error && (
            <p
              id="validate-idea-error"
              className="font-sans text-sm"
              style={{ color: "var(--color-contradicted-fg)" }}
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
        <p
          id="validate-idea-count"
          className="font-sans text-xs tabular-nums"
          style={{ color: charCountColor }}
          aria-live="polite"
        >
          {charCount} / {MAX_CHARS}
        </p>
      </div>

      <button
        onClick={handleSubmit}
        disabled={charCount === 0}
        className="accent-btn inline-flex w-full sm:w-auto items-center justify-center px-[var(--space-6)] py-[var(--space-3)] rounded-[var(--radius-md)] font-sans text-sm font-semibold uppercase tracking-[0.08em] disabled:opacity-40 disabled:cursor-not-allowed mb-[var(--space-3)]"
      >
        START VALIDATION
      </button>

      <p
        className="font-sans"
        style={{
          fontSize: "var(--text-xs)",
          color: "var(--text-muted)",
        }}
      >
        Takes 1-2 hours. ProveIt will ask questions, then research.
      </p>
    </div>
  );
}

// ─── Main ChatInterface ────────────────────────────────────────────────────────

export default function ChatInterface() {
  const { session: storedSession, updateSession } = useSession();
  const { isStreaming, error: streamError, errorReason: streamErrorReason, startStream, stopStream } = useStream();

  const [activeSession, setActiveSession] = useState<ValidationSession | null>(null);
  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchLog, setSearchLog] = useState<string[]>([]);
  const [currentQuery, setCurrentQuery] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const phaseRef = useRef<DiscoveryPhase>("brain_dump");
  const scoresRef = useRef<ConfidenceScores>({
    desirability: null,
    viability: null,
    feasibility: null,
  });
  const killSignalsRef = useRef<KillSignal[]>([]);

  // Resume from localStorage
  const handleResume = useCallback(() => {
    if (storedSession) {
      setActiveSession(storedSession);
      phaseRef.current = storedSession.phase;
      scoresRef.current = storedSession.scores;
      killSignalsRef.current = storedSession.killSignals;
      setShowChat(true);
    }
  }, [storedSession]);

  // Start a new session
  const handleStart = useCallback((idea: string) => {
    const session = createSession(idea);
    setActiveSession(session);
    phaseRef.current = session.phase;
    scoresRef.current = session.scores;
    killSignalsRef.current = [];
    setShowChat(true);

    captureEvent("validation_started", { idea_length: idea.length });

    // Immediately send first user message and get ProveIt's opening
    sendMessage(idea, session);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = useCallback(
    async (text: string, sessionOverride?: ValidationSession) => {
      const currentSession = sessionOverride ?? activeSession;
      if (!currentSession) return;

      // Build updated messages with the new user message
      const userMsg: Message = {
        id: nanoid(),
        role: "user",
        content: text,
        timestamp: Date.now(),
      };

      const updatedMessages: Message[] = [
        ...currentSession.messages,
        userMsg,
      ];

      // Optimistically update session with user message
      const sessionWithUser: ValidationSession = {
        ...currentSession,
        messages: updatedMessages,
        updatedAt: Date.now(),
      };
      setActiveSession(sessionWithUser);

      // Reset streaming state
      setCurrentMessage("");
      setChatError(null);
      setIsSearching(false);
      setSearchLog([]);
      setCurrentQuery(null);

      // Build request payload (strip client-only fields)
      const payload = {
        sessionId: currentSession.id,
        messages: updatedMessages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        phase: phaseRef.current,
        scores: scoresRef.current,
      };

      let accumulatedText = "";
      let newPhase = phaseRef.current;
      let newScores = { ...scoresRef.current };
      let newKillSignals = [...killSignalsRef.current];

      const handleText = (chunk: string) => {
        accumulatedText += chunk;
        setCurrentMessage(accumulatedText);
      };

      const handleEvent = (event: StreamEvent) => {
        if (event.type === "search_query") {
          setCurrentQuery(event.query);
        } else if (event.type === "searching") {
          setIsSearching(event.active);
          if (!event.active) {
            // search finished — move current query to completed log
            setCurrentQuery((q) => {
              if (q) setSearchLog((prev) => [...prev, q]);
              return null;
            });
          }
        } else if (event.type === "phase_change") {
          newPhase = event.phase;
          phaseRef.current = event.phase;
          setActiveSession((prev) =>
            prev ? { ...prev, phase: event.phase, updatedAt: Date.now() } : prev
          );
          captureEvent("validation_phase_changed", { phase: event.phase });
          if (event.phase === "complete") {
            captureEvent("validation_completed");
          } else if (event.phase === "findings") {
            captureEvent("validation_research_complete");
          }
        } else if (event.type === "scores") {
          newScores = event.scores;
          scoresRef.current = event.scores;
          setActiveSession((prev) =>
            prev
              ? { ...prev, scores: event.scores, updatedAt: Date.now() }
              : prev
          );
        } else if (event.type === "kill_signal") {
          newKillSignals = [
            ...newKillSignals,
            {
              ...event.signal,
              detectedAt: updatedMessages.length,
            },
          ];
          killSignalsRef.current = newKillSignals;
          setActiveSession((prev) =>
            prev
              ? {
                  ...prev,
                  killSignals: newKillSignals,
                  updatedAt: Date.now(),
                }
              : prev
          );
        } else if (event.type === "error") {
          setChatError(event.message);
        } else if (event.type === "done") {
          setIsSearching(false);
        }
      };

      await startStream("/api/chat", payload, handleText, handleEvent);

      // Stream complete — commit assistant message to session
      if (accumulatedText.trim().length > 0) {
        const assistantMsg: Message = {
          id: nanoid(),
          role: "assistant",
          content: accumulatedText,
          timestamp: Date.now(),
        };

        const finalSession: ValidationSession = {
          ...sessionWithUser,
          messages: [...updatedMessages, assistantMsg],
          phase: newPhase,
          scores: newScores,
          killSignals: newKillSignals,
          researchComplete: newPhase === "findings" || newPhase === "complete",
          updatedAt: Date.now(),
        };

        setActiveSession(finalSession);
        updateSession(finalSession);

        // Auto-trigger: when discovery transitions to research, immediately kick off the research stream
        if (newPhase === "research") {
          setCurrentMessage("");
          setIsSearching(false);
          setSearchLog([]);
          setCurrentQuery(null);

          const researchPayload = {
            sessionId: finalSession.id,
            messages: finalSession.messages.map((m) => ({
              role: m.role,
              content: m.content,
            })),
            phase: "research" as DiscoveryPhase,
            scores: newScores,
          };

          let researchText = "";
          await startStream(
            "/api/chat",
            researchPayload,
            (chunk) => {
              researchText += chunk;
              setCurrentMessage(researchText);
            },
            handleEvent
          );

          if (researchText.trim().length > 0) {
            const researchMsg: Message = {
              id: nanoid(),
              role: "assistant",
              content: researchText,
              timestamp: Date.now(),
            };
            // Use refs for post-stream values — handleEvent mutates them during the stream
            const finalPhase = phaseRef.current;
            const researchSession: ValidationSession = {
              ...finalSession,
              messages: [...finalSession.messages, researchMsg],
              phase: finalPhase,
              scores: scoresRef.current,
              killSignals: killSignalsRef.current,
              researchComplete: finalPhase === "findings" || finalPhase === "complete",
              updatedAt: Date.now(),
            };
            setActiveSession(researchSession);
            updateSession(researchSession);
          }
        }
      }

      setCurrentMessage("");
      setIsSearching(false);
      setSearchLog([]);
      setCurrentQuery(null);
    },
    [activeSession, startStream, updateSession]
  );

  const handleUserSend = useCallback(
    (text: string) => {
      if (activeSession) {
        sendMessage(text);
      }
    },
    [activeSession, sendMessage]
  );

  const handleStop = useCallback(() => {
    stopStream();
    setIsSearching(false);
    // Commit partial message if any
    if (currentMessage.trim().length > 0 && activeSession) {
      const partialMsg: Message = {
        id: nanoid(),
        role: "assistant",
        content: currentMessage,
        timestamp: Date.now(),
      };
      const finalSession: ValidationSession = {
        ...activeSession,
        messages: [...activeSession.messages, partialMsg],
        updatedAt: Date.now(),
      };
      setActiveSession(finalSession);
      updateSession(finalSession);
    }
    setCurrentMessage("");
  }, [stopStream, currentMessage, activeSession, updateSession]);

  const handleTimeout = useCallback(() => {
    setChatError("Search is taking too long. Please try again.");
    stopStream();
    setIsSearching(false);
  }, [stopStream]);

  // Show input form if no active session
  if (!showChat || !activeSession) {
    return (
      <IdeaInputForm onStart={handleStart} onResume={handleResume} />
    );
  }

  const isComplete = activeSession.phase === "complete";
  const canDownload = activeSession.phase === "findings" || activeSession.phase === "complete";
  const hasScorePanelContent =
    activeSession.scores.desirability !== null ||
    activeSession.scores.viability !== null ||
    activeSession.scores.feasibility !== null ||
    activeSession.killSignals.length > 0;

  return (
    <div className="flex flex-col lg:flex-row flex-1" style={{ maxHeight: "calc(100vh - 56px)" }}>
      {/* Chat column */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Compact idea bar + phase indicator */}
        <div
          className="flex items-center justify-between px-[var(--space-4)] py-[var(--space-3)] border-b shrink-0"
          style={{
            backgroundColor: "var(--bg-surface)",
            borderColor: "var(--border-subtle)",
          }}
        >
          <p
            className="font-sans truncate mr-[var(--space-4)]"
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--text-secondary)",
            }}
          >
            <span style={{ color: "var(--text-muted)" }}>Idea: </span>
            {activeSession.ideaSummary.length > 60
              ? activeSession.ideaSummary.slice(0, 60) + "..."
              : activeSession.ideaSummary}
          </p>
          <PhaseIndicator phase={activeSession.phase} />
        </div>

        {/* Messages area */}
        <div
          className="flex-1 overflow-y-auto px-[var(--space-4)] md:px-[var(--space-8)] py-[var(--space-6)]"
          style={{ maxWidth: "720px", width: "100%", margin: "0 auto" }}
        >
          <MessageList
            messages={activeSession.messages}
            streamingMessage={isStreaming && currentMessage ? currentMessage : null}
            beforeStreaming={
              (isSearching || searchLog.length > 0) ? (
                <SearchingIndicator
                  isSearching={isSearching}
                  searches={searchLog}
                  currentQuery={currentQuery}
                  onTimeout={handleTimeout}
                />
              ) : undefined
            }
          />

          {/* Error display */}
          {(chatError || streamError) && (
            <div
              className="mt-[var(--space-4)] rounded-[var(--radius-lg)] border p-[var(--space-4)]"
              style={{
                borderColor: streamErrorReason ? "var(--border-default)" : "var(--color-contradicted-fg)",
                backgroundColor: streamErrorReason ? "var(--bg-surface)" : "var(--color-contradicted)",
              }}
              role="alert"
            >
              <p
                className="font-sans text-sm"
                style={{
                  color: streamErrorReason ? "var(--text-primary)" : "var(--color-contradicted-fg)",
                }}
              >
                {chatError || streamError}
              </p>
              {streamErrorReason && (
                <EmailCaptureForm
                  reason={streamErrorReason}
                  ideaExcerpt={activeSession.ideaSummary}
                />
              )}
            </div>
          )}

          {/* Download button — available from findings phase onward */}
          {canDownload && (
            <div className="mt-[var(--space-6)] flex flex-col gap-[var(--space-4)]">
              <div className="flex flex-col sm:flex-row gap-[var(--space-4)]">
                <DownloadButton session={activeSession} />
              </div>
              <FullBundlePointer session={activeSession} />
            </div>
          )}
        </div>

        {/* Score panel — mobile inline placement, between messages and chat input.
            Suppressed during Brain Dump / Discovery when no scores have arrived yet
            (the empty-state filler wastes vertical real estate on small screens).
            Hidden on lg:+ where the sidebar at right takes over. */}
        {hasScorePanelContent && (
          <div
            className="lg:hidden border-t shrink-0 max-h-[40vh] overflow-y-auto p-[var(--space-4)]"
            style={{ borderColor: "var(--border-subtle)" }}
          >
            <ScorePanel
              scores={activeSession.scores}
              killSignals={activeSession.killSignals}
              phase={activeSession.phase}
            />
          </div>
        )}

        {/* Chat input bar — sticky bottom */}
        <div className="shrink-0">
          <ChatInput
            onSend={handleUserSend}
            onStop={handleStop}
            isStreaming={isStreaming}
            isDisabled={isComplete}
            phase={activeSession.phase}
          />
        </div>
      </div>

      {/* Score panel — sidebar on desktop only */}
      <div
        className="hidden lg:block lg:w-72 xl:w-80 lg:border-l p-[var(--space-4)] overflow-y-auto"
        style={{ borderColor: "var(--border-subtle)" }}
      >
        <ScorePanel
          scores={activeSession.scores}
          killSignals={activeSession.killSignals}
          phase={activeSession.phase}
        />
      </div>
    </div>
  );
}
